import { RotateCcw, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type {
  DataTableAction,
  DataTableColumn,
} from '@/components/ui/data-table'
import { Status, StatusIndicator, StatusLabel } from '@/components/ui/status'
import type { TeamMemberOrInvitation } from '@/services/invitations'
import { getAvatarColor, getInitials } from './settings-utils'

export const teamColumns: DataTableColumn<TeamMemberOrInvitation>[] = [
  {
    key: 'member',
    label: 'Member',
    sortable: true,
    className: '!px-6 !py-3',
    headerClassName: '!px-6 !py-3',
    render: (item: TeamMemberOrInvitation) => {
      const isUser = item.type === 'user'
      const name = isUser ? item.fullName : item.email
      const email = isUser ? item.email : item.email
      const initials = isUser
        ? getInitials(item.fullName)
        : getInitials(item.email.split('@')[0])
      const avatarColor = getAvatarColor(item.id)

      return (
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full bg-gradient-to-r ${avatarColor} flex items-center justify-center text-white font-medium`}
          >
            {initials}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-foreground">
                {name}
              </span>
              {!isUser && (
                <Status
                  status={item.status === 'expired' ? 'error' : 'degraded'}
                >
                  <StatusIndicator />
                  <StatusLabel>
                    {item.status === 'expired' ? 'Expired' : 'Pending'}
                  </StatusLabel>
                </Status>
              )}
            </div>
            <div className="text-sm text-muted-foreground">{email}</div>
          </div>
        </div>
      )
    },
  },
  {
    key: 'joined',
    label: 'Joined',
    sortable: true,
    className: '!px-6 !py-3',
    headerClassName: '!px-6 !py-3',
    render: (item: TeamMemberOrInvitation) => {
      const isUser = item.type === 'user'
      let date: string
      let label: string

      if (isUser) {
        date = item.lastSignInAt || new Date().toISOString()
        label = 'Joined'
      } else {
        date = item.createdAt
        label = 'Invited'
      }

      return (
        <div className="flex flex-col">
          <span className="text-sm font-normal text-foreground">
            {new Date(date).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
      )
    },
  },
]

// Create team actions dynamically based on permissions
export const getTeamActions = (
  item: TeamMemberOrInvitation,
  hasGodModePermissions: boolean,
  removeUser: (id: string) => Promise<void>,
  revokeInvitation: (id: string) => Promise<void>,
  resendInvitation: (id: string) => Promise<void>
): DataTableAction<TeamMemberOrInvitation>[] => {
  const actions: DataTableAction<TeamMemberOrInvitation>[] = []

  if (item.type === 'invitation') {
    // Actions for pending invitations
    actions.push({
      label: 'Resend invitation',
      icon: <RotateCcw className="h-4 w-4" />,
      onClick: async () => {
        try {
          await resendInvitation(item.id)
          toast.success('Invitation resent successfully')
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to resend invitation'
          toast.error(errorMessage)
        }
      },
      variant: 'default',
    })

    actions.push({
      label: 'Revoke invitation',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: async () => {
        try {
          await revokeInvitation(item.id)
          toast.success('Invitation revoked successfully')
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to revoke invitation'
          toast.error(errorMessage)
        }
      },
      variant: 'destructive',
    })
  } else {
    // Actions for existing users
    if (hasGodModePermissions) {
      actions.push({
        label: 'Remove user',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: async () => {
          try {
            await removeUser(item.id)
            toast.success('User removed successfully')
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Failed to remove user'
            toast.error(errorMessage)
          }
        },
        variant: 'destructive',
      })
    }
  }

  return actions
}
